import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { format, subDays, subMonths } from "date-fns";
import { sv } from "date-fns/locale";
import { CheckCircle2, XCircle } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import { cn } from "@/lib/utils";

const matchTypeLabels: Record<string, string> = {
  casual: "Fri match",
  league: "Serie",
  tournament: "Turnering",
  open_match: "Aktivitet",
};

export default function MatchHistorySection({ userId }: { userId?: string }) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const playerId = userId || user?.id;
  const [typeFilter, setTypeFilter] = useState("all");
  const [resultFilter, setResultFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");

  const { data: matches = [], isLoading } = useQuery({
    queryKey: ["match-history", playerId, organization?.id, typeFilter, periodFilter],
    queryFn: async () => {
      let q = supabase
        .from("match_results")
        .select("*")
        .eq("organization_id", organization!.id)
        .or(`winner_team.cs.{${playerId}},loser_team.cs.{${playerId}}`)
        .order("played_at", { ascending: false })
        .limit(50);

      if (typeFilter !== "all") {
        q = q.eq("match_type", typeFilter);
      }

      if (periodFilter !== "all") {
        const now = new Date();
        let from: Date;
        if (periodFilter === "week") from = subDays(now, 7);
        else if (periodFilter === "month") from = subMonths(now, 1);
        else from = subMonths(now, 3);
        q = q.gte("played_at", from.toISOString());
      }

      const { data, error } = await q;
      if (error) throw error;

      let results = data ?? [];
      if (resultFilter === "win") {
        results = results.filter((m) => (m.winner_team as string[]).includes(playerId!));
      } else if (resultFilter === "loss") {
        results = results.filter((m) => (m.loser_team as string[]).includes(playerId!));
      }
      return results;
    },
    enabled: !!playerId && !!organization?.id,
  });

  // Fetch all player profiles involved (with level for LevelBadge)
  const playerIds = [...new Set(matches.flatMap((m) => [...(m.winner_team as string[] || []), ...(m.loser_team as string[] || [])]))];
  const { data: profiles = [] } = useQuery({
    queryKey: ["match-profiles", playerIds.join(",")],
    queryFn: async () => {
      if (playerIds.length === 0) return [];
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level")
        .in("id", playerIds);
      return data ?? [];
    },
    enabled: playerIds.length > 0,
  });

  const profileMap = new Map(profiles.map((p: { id: string; [key: string]: unknown }) => [p.id, p]));
  const getName = (id: string) => profileMap.get(id)?.full_name || "Okänd";
  const getLevel = (id: string) => profileMap.get(id)?.level ?? 1;

  if (isLoading) return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-3 gap-2">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Typ" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla typer</SelectItem>
            <SelectItem value="casual">Fri match</SelectItem>
            <SelectItem value="league">Serie</SelectItem>
            <SelectItem value="tournament">Turnering</SelectItem>
            <SelectItem value="open_match">Aktivitet</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resultFilter} onValueChange={setResultFilter}>
          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Resultat" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla</SelectItem>
            <SelectItem value="win">Vinster</SelectItem>
            <SelectItem value="loss">Förluster</SelectItem>
          </SelectContent>
        </Select>
        <Select value={periodFilter} onValueChange={setPeriodFilter}>
          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Period" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla</SelectItem>
            <SelectItem value="week">Senaste veckan</SelectItem>
            <SelectItem value="month">Senaste månaden</SelectItem>
            <SelectItem value="quarter">Senaste 3 mån</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {matches.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm py-8">Ingen matchhistorik</p>
      ) : (
        <div className="space-y-2">
          {matches.map((m) => {
            const isWin = (m.winner_team as string[]).includes(playerId!);
            const opponents = (isWin ? m.loser_team : m.winner_team) as string[];
            return (
              <Card key={m.id}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={cn(
                    "h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                    isWin ? "bg-green-500/10" : "bg-red-500/10"
                  )}>
                    {isWin ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">
                        vs {opponents.map(getName).join(" & ")}
                      </p>
                      {opponents.slice(0, 1).map((opId) => (
                        <LevelBadge key={opId} level={getLevel(opId)} size="sm" />
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono">{m.score}</span>
                      <span>·</span>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                        {matchTypeLabels[m.match_type] || m.match_type}
                      </Badge>
                      {!m.confirmed && <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-amber-500/50 text-amber-600">Ej bekräftad</Badge>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant={isWin ? "default" : "secondary"} className="text-[10px] mb-0.5">
                      {isWin ? "V" : "F"}
                    </Badge>
                    <p className="text-[10px] text-muted-foreground">
                      {format(new Date(m.played_at), "d MMM", { locale: sv })}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
